import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import type { Celebration, CelebrationKind } from './celebrations';
import { CELEBRATION_ICONS, CELEBRATION_LABEL_KEYS } from './celebration-kinds';

interface CelebrationControlsProps {
  /** The celebration whose pin was tapped on the timeline, if any. */
  selected: Celebration | null;
  /** The kinds it may be given: the ones that light or grow the card need it on the media. */
  kinds: CelebrationKind[];
  onChange: (id: string, kind: CelebrationKind) => void;
  onRemove: (id: string) => void;
}

/**
 * The row that comes up over the editor's timeline once a pin on it is
 * tapped: the way to give that celebration another kind or take it away.
 * Nothing otherwise: new ones are put down from the editor's side column
 * (`CelebrateDialog`), and the pins themselves are on the timeline
 * (`VideoTrimBar`).
 */
export function CelebrationControls({ selected, kinds, onChange, onRemove }: CelebrationControlsProps) {
  const { t } = useTranslation();
  if (!selected) return null;

  const kindButtons = (onPick: (kind: CelebrationKind) => void, current: CelebrationKind) =>
    kinds.map((kind) => {
      const Icon = CELEBRATION_ICONS[kind];
      return (
        <Button
          key={kind}
          size="sm"
          variant={kind === current ? 'default' : 'outline'}
          className={`gap-1.5 ${kind === current ? '' : 'border-white/30 bg-black/40 text-white hover:bg-black/60 hover:text-white'}`}
          onClick={() => onPick(kind)}
          data-testid={`celebrate-${kind}`}
        >
          <Icon className="size-4" aria-hidden />
          {t(CELEBRATION_LABEL_KEYS[kind])}
        </Button>
      );
    });

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="celebration-controls">
      {kindButtons((kind) => onChange(selected.id, kind), selected.kind)}
      <Button
        size="sm"
        variant="ghost"
        className="gap-1.5 text-red-300 hover:bg-black/60 hover:text-red-200"
        onClick={() => onRemove(selected.id)}
        data-testid="celebration-remove"
      >
        <Trash2 className="size-4" aria-hidden />
        {t('shareClimb.removeCelebration')}
      </Button>
    </div>
  );
}
